import React from "react";

import { AppBar, Toolbar, IconButton, Typography, Grid, Paper, Hidden, Input, List, MenuItem, ListItemText, ListItemAvatar, makeStyles } from "@material-ui/core";

import ArrowBackIos from "@material-ui/icons/ArrowBackIos";
import PresentToAllIcon from "@material-ui/icons/PresentToAll";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import PrintIcon from "@material-ui/icons/Print";
import ShareIcon from "@material-ui/icons/Share";
import FavoriteBorder from "@material-ui/icons/FavoriteBorder";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import OnlineIcon from "@material-ui/icons/CheckCircleOutline";
import FolderIcon from "@material-ui/icons/Folder";
import StarIcon from "@material-ui/icons/Star";
import OfflineIcon from "@material-ui/icons/CheckCircle";
import PDFIcon from "@material-ui/icons/PictureAsPdf";
import DescriptionIcon from "@material-ui/icons/Description";
import PresentSlidesIcon from "@material-ui/icons/Panorama";

import { UserAvatar } from "../../../components/UserAvatar";
import { BuilderAsset } from "../../../entities/BuilderAsset";
import { TelemetryService } from "../../../services/TelemetryService";
import { BuilderAssetService } from "../../../services/BuilderAssetService";
import { ServerResponse } from "../../../services/server/OldWebClient";
import { ExportHtml } from "../../../utilities/ExportHtml";
import { EventTracking, OfflineActionLocation, PrintActions } from "../../../utilities/Tracking/EventTracking";

import { DesktopBuilderAppSelection } from "../drawer/apps/DesktopBuilderAppSelection";
import { TextToSpeechPlayer } from "../editor/texttospeech/TextToSpeechPlayer";
import { CKEditor } from "../editor/ckeditor/CKEditor";
import { useEditor } from "../editor/useEditor";
import { useBuilderDrawer } from "../drawer/useBuilderDrawer";
import { useOfflineAssets } from "../../offline/useOfflineAssets";
import { BuilderAppBarAction, useBuilder } from "../useBuilder";
import { DropDownButton } from "../../../components/DropDownButton";
import { exportSlides } from "../../../utilities/ExportSlides";
import classNames from "classnames";
import { useState } from "react";
import { ReactComponent as OneColumn } from "../../../resources/oneColumn.svg";
import { ReactComponent as TwoColumns } from "../../../resources/twoColumns.svg";
import { ReactComponent as ThreeColumns } from "../../../resources/threeColumns.svg";
import PPTIcon from "../../../resources/file-ppt.png";
import { UserAgent } from "../../../utilities/UserAgent";

const drawerWidth = 350;

const useStyles = makeStyles((theme) => ({
	root: {
		background: "#efefef",
		height: "100vh",
		overflow: "hidden",
		flexGrow: 1,
	},
	grow: {
		flex: 1,
	},
	appBar: {
		color: "#6b6b6b",
		background: "#FFFFFF",
		boxShadow: "unset",
		zIndex: theme.zIndex.drawer + 1,
		transition: theme.transitions.create(["margin", "width"], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
	},
	appBarShift: {
		width: `calc(100% - ${drawerWidth}px)`,
		transition: theme.transitions.create(["margin", "width"], {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.enteringScreen,
		}),
		marginRight: drawerWidth,
	},
	toolbar: theme.mixins.toolbar,
	userIcon: {
		background: "#2196f3",
		width: "52px",
		float: "right",
	},
	drawerButtonSlidesTitle: {
		background: "#666",
		color: "#FFF",
		padding: 10,
		textAlign: "left",
		position: "fixed",
		bottom: 0,
		fontSize: 17,
		zIndex: 1,
		borderRadius: "0",
		textTransform: "none",
		width: 300,
		"&:hover": {
			background: "#666666d9",
		},
	},
	drawerButtonExploreTitle: {
		background: "#666",
		color: "#FFF",
		textAlign: "left",
		position: "fixed",
		bottom: 0,
		right: 0,
		fontSize: 17,
		borderRadius: "0",
		padding: 10,
		textTransform: "none",
		width: 410,
		"&:hover": {
			background: "#666666d9",
		},
	},
	editorContainer: {
		height: `calc(100vh - ${theme.mixins.toolbar.minHeight}px - 30px)`,
	},
	mainPaper: {
		height: "100%",
		marginTop: 10,
		marginBottom: 10,
		position: "relative",
		width: 750,
	},
	editorWrapper: {
		height: "95%",
	},
	editor: {
		height: "100%",
	},
	mainToolbar: {
		padding: "0 14px",
	},
	closeIcon: {
		float: "right",
	},
	title: {
		maxWidth: "539px",
		textOverflow: "ellipsis",
		overflow: "hidden",
		cursor: "pointer",
		whiteSpace: "nowrap",
	},
	description: {
		maxWidth: "539px",
		textOverflow: "ellipsis",
		overflow: "hidden",
		whiteSpace: "nowrap",
	},
	printGrid: {
		width: 300,
	},
	menuIcon: {
		color: "rgba(0, 0, 0, 0.54)",
	},
	columns: {
		border: "1px solid rgba(0, 0, 0, 0.24)",
		borderRadius: "5%",
		padding: 5,
		webkitBoxShadow: "0px 0px 7px 3px rgba(0,0,0,0.1)",
		MozBoxShadow: "0px 0px 7px 3px rgba(0,0,0,0.1)",
		boxShadow: "0px 0px 7px 3px rgba(0,0,0,0.1)",
		"&:hover": {
			transform: "translateY(-5px) scale(1.01) translateZ(0)",
			cursor: "pointer",
		},
		filter: "contrast(0)",
	},
}));

const currentLocation = "Navigation Desktop";

export function DesktopView() {
	const builder = useBuilder();
	const { builderAsset, user, preferences } = builder;
	const classes = useStyles();
	const builderDrawer = useBuilderDrawer();
	const editor = useEditor();
	const { onlineMode, setAssetOfflineMode } = useOfflineAssets();

	const hasSlides = editor.slides.length !== 0;
	const asset = builderAsset;

	const [assetTitle, setAssetTitle] = useState<string>(asset.name);
	const [editingTitleInToolbar, setEditingTitleInToolbar] = React.useState(false);

	const onStopEditingTitle = () => {
		setEditingTitleInToolbar(false);
		setAssetTitle(asset.name);
	};

	const onClickAssetTitle = () => {
		if (!onlineMode) {
			return;
		}
		setEditingTitleInToolbar(true);
		setAssetTitle(assetTitle !== asset.name ? asset.name : assetTitle);
	};

	const onDownloadAssetAsPdf = () => {
		BuilderAssetService.downloadAsPdf(asset);

		let now = new Date().toString();
		TelemetryService.updateContactField("sc_cloud_downloadpdf", now);
		EventTracking.Builder.onDownloadSermonAsPdf(currentLocation);
	};

	const onDownloadAssetAsDocx = () => {
		BuilderAssetService.downloadAsDocx(asset);
		EventTracking.Builder.onDownloadSermonAsDocx(currentLocation);
	};

	const onDownloadSlidePresentation = () => {
		exportSlides(builderAsset, editor.slides, preferences);

		let now = new Date().toString();
		TelemetryService.updateContactField("sc_cloud_slideexport", now);
		EventTracking.Builder.onExportSlides(currentLocation);
	};

	const onPrintAsset = (columns: number, action: PrintActions) => {
		EventTracking.Builder.onPrint(action, currentLocation);
		let now = new Date();
		TelemetryService.updateContactField("sc_cloud_print", now.toString());
		const html = editor.getHtml();
		ExportHtml.print(html, columns);
	};

	const onSaveAssetTitle = async () => {
		const asset: BuilderAsset = { ...builderAsset, name: assetTitle };
		let response = await BuilderAssetService.save(asset);
		if (ServerResponse.isSuccess<BuilderAsset>(response)) {
			builder.setBuilderAsset(response.data);
		} else {
			builder.setAlert("Could not update title");
		}
		setEditingTitleInToolbar(false);
	};

	const toggleOfflineAsset = async (location: OfflineActionLocation) => {
		const response = await setAssetOfflineMode(builderAsset.id, !builderAsset.offlineMode, location);
		if (response) {
			builder.setBuilderAsset(response);
			builder.setAlert(response.offlineMode ? "Available offline" : "No longer available offline");
		}
	};

	const toggleStar = async () => {
		const updatedAsset: BuilderAsset = { ...builderAsset, isStarred: !builderAsset.isStarred };
		let response = await BuilderAssetService.save(updatedAsset);
		if (ServerResponse.isSuccess<BuilderAsset>(response)) {
			builder.setBuilderAsset(response.data);
			builder.setAlert(response.data.isStarred ? "Star Added" : "Star Removed");
		} else {
			builder.setAlert("Could not update Starred");
		}
		setEditingTitleInToolbar(false);
	};

	return (
		<>
			<div className={classes.root}>
				<AppBar
					position="fixed"
					className={classNames(classes.appBar, {
						[classes.appBarShift]: builderDrawer.drawerOpen && onlineMode,
					})}
				>
					<Toolbar className={classes.mainToolbar}>
						<IconButton
							onClick={() => builder.handleAppBarAction(BuilderAppBarAction.GoBack, currentLocation)}
							color="inherit"
							aria-label="Menu"
						>
							<ArrowBackIos />
						</IconButton>
						<Grid container alignItems="center">
							{editingTitleInToolbar ? (
								<Grid item>
									<div style={{ display: "flex", justifyContent: "space-between" }}>
										<div>
											<Input
												style={{ minWidth: 300 }}
												onKeyDown={async (e) => {
													if (e.key === "Enter") {
														await onSaveAssetTitle();
													}
												}}
												placeholder="Title"
												value={assetTitle}
												onChange={(e) => setAssetTitle(e.target.value)}
												inputProps={{
													"aria-label": "Name",
												}}
											/>
										</div>

										<IconButton onClick={async () => await onSaveAssetTitle()} className={classes.closeIcon}>
											<CheckIcon />
										</IconButton>
										<IconButton onClick={onStopEditingTitle} className={classes.closeIcon}>
											<CloseIcon />
										</IconButton>
									</div>
								</Grid>
							) : (
								<>
									<Grid item>
										<Grid container direction="column" justifyContent="center">
											<Grid item>
												<Typography className={classes.title} variant="h6" color="inherit" onClick={onClickAssetTitle}>
													{asset.name}{" "}
													{asset.isTemplate && (
														<span style={{ fontSize: "12px", fontWeight: "bold", color: "#d90000" }}>
															{" "}
															- Editing Template
														</span>
													)}
												</Typography>
												{asset.isTemplate && (
													<Typography variant="body2" color="inherit" className={classes.description}>
														Description:{" "}
														{asset.templateDescription.length > 0 ? asset.templateDescription : "No Description"}
													</Typography>
												)}
											</Grid>
										</Grid>
									</Grid>
									{!asset.isTemplate && (
										<Grid item>
											<Grid container justifyContent="center">
												<Grid item>
													<Hidden mdDown>
														<Typography
															variant="caption"
															color="textSecondary"
															style={{
																display: "inline",
																verticalAlign: "text-top",
																padding: window.innerWidth > 1230 ? 8 : undefined,
															}}
														>
															{onlineMode
																? editor.saveInProgress
																	? "Saving..."
																	: editor.lastEdit
																: editor.saveInProgress
																? "Saving..."
																: editor.lastEdit + " - Offline Mode"}
														</Typography>
													</Hidden>
													{!onlineMode && <OfflineIcon style={{ paddingLeft: 5, verticalAlign: "sub" }} />}
													{onlineMode && builderAsset.offlineMode && (
														<OfflineIcon
															style={{ paddingLeft: 5, verticalAlign: "sub", cursor: "pointer" }}
															onClick={() => toggleOfflineAsset("App Bar Icon (Builder)")}
														/>
													)}
													{onlineMode && !builderAsset.offlineMode && (
														<OnlineIcon
															style={{ paddingLeft: 5, verticalAlign: "sub", cursor: "pointer" }}
															onClick={() => toggleOfflineAsset("App Bar Icon (Builder)")}
														/>
													)}
												</Grid>
											</Grid>
										</Grid>
									)}
								</>
							)}
						</Grid>
						<div className={classes.grow} />
						<Grid container justifyContent="flex-end" wrap="nowrap">
							<Grid item>
								<DropDownButton
									buttonText={"File"}
									icon={<FolderIcon />}
									displayBody={
										<>
											{!asset.isTemplate && (
												<>
													<MenuItem
														onClick={() =>
															builder.handleAppBarAction(BuilderAppBarAction.SaveAsTemplate, currentLocation)
														}
													>
														<ListItemAvatar>
															<FavoriteBorder className={classes.menuIcon} />
														</ListItemAvatar>
														<ListItemText>Save as template</ListItemText>
													</MenuItem>
													{/* <MenuItem onClick={() => builder.handleAppBarAction(BuilderAppBarAction.Share, currentLocation)}>
														<ListItemAvatar>
															<ShareIcon className={classes.menuIcon} />
														</ListItemAvatar>
														<ListItemText>Share</ListItemText>
													</MenuItem> */}
												</>
											)}
											<MenuItem onClick={() => toggleOfflineAsset("File Dropdown (Builder)")}>
												<ListItemAvatar>
													<OfflineIcon className={classes.menuIcon} />
												</ListItemAvatar>
												<ListItemText>Offline {builderAsset.offlineMode ? "Disable" : "Enable"}</ListItemText>
											</MenuItem>
											<MenuItem onClick={toggleStar}>
												<ListItemAvatar>
													<StarIcon className={classes.menuIcon} />
												</ListItemAvatar>
												<ListItemText>{builderAsset.isStarred ? "Remove" : "Add"} Star</ListItemText>
											</MenuItem>
										</>
									}
								/>
							</Grid>
							{!asset.isTemplate && (
								<Grid item>
									<DropDownButton
										buttonText={"Present"}
										icon={<PresentToAllIcon />}
										displayBody={
											<List>
												<MenuItem onClick={() => builder.handleAppBarAction(BuilderAppBarAction.Present, currentLocation)}>
													<ListItemAvatar>
														<PresentToAllIcon className={classes.menuIcon} />
													</ListItemAvatar>
													<ListItemText>Present Sermon</ListItemText>
												</MenuItem>
												<MenuItem
													onClick={() => builder.toggleSlideExportDialog(currentLocation)}
													disabled={!hasSlides}
												>
													<ListItemAvatar>
														<PresentSlidesIcon className={classes.menuIcon} />
													</ListItemAvatar>
													<ListItemText>Present Slides</ListItemText>
												</MenuItem>
											</List>
										}
									/>
								</Grid>
							)}
							<Grid item>
								<DropDownButton
									buttonText={"Download"}
									icon={<CloudDownloadIcon />}
									displayBody={
										<List>
											<MenuItem onClick={onDownloadAssetAsPdf}>
												<ListItemAvatar>
													<PDFIcon className={classes.menuIcon} />
												</ListItemAvatar>
												<ListItemText>Download as PDF</ListItemText>
											</MenuItem>
											<MenuItem onClick={onDownloadAssetAsDocx}>
												<ListItemAvatar>
													<DescriptionIcon className={classes.menuIcon} />
												</ListItemAvatar>
												<ListItemText>Download as Docx</ListItemText>
											</MenuItem>
											<MenuItem onClick={onDownloadSlidePresentation} disabled={!hasSlides}>
												<ListItemAvatar>
													<img alt="powerpoint icon" src={PPTIcon} height={24} width={24} className={classes.menuIcon} />
												</ListItemAvatar>
												<ListItemText>Download slide presentation</ListItemText>
											</MenuItem>
										</List>
									}
								/>
							</Grid>
							<Grid item>
								<DropDownButton
									buttonText={"Print"}
									icon={<PrintIcon />}
									displayBody={
										<div style={{ width: 300, marginBottom: 10 }}>
											<List>
												<MenuItem onClick={() => onPrintAsset(1, "Quick Print")}>
													<ListItemAvatar>
														<PrintIcon className={classes.menuIcon} />
													</ListItemAvatar>
													<ListItemText>Quick Print</ListItemText>
												</MenuItem>
												{UserAgent.getBrowser().name !== "Safari" && (
													<>
														<ListItemText style={{paddingLeft: 16}}>Print with columns</ListItemText>
														<Grid
															container
															direction="row"
															alignItems="center"
															justifyContent="center"
															spacing={2}
															style={{ marginTop: 5, paddingBottom: 5 }}
														>
															<Grid item onClick={() => onPrintAsset(1, "Single Column")}>
																<OneColumn height={75} width={75} className={classes.columns} />
															</Grid>
															<Grid item onClick={() => onPrintAsset(2, "Two Column")}>
																<TwoColumns height={75} width={75} className={classes.columns} />
															</Grid>
															<Grid item onClick={() => onPrintAsset(3, "Three Column")}>
																<ThreeColumns height={75} width={75} className={classes.columns} />
															</Grid>
														</Grid>
													</>
												)}
											</List>
										</div>
									}
								/>
							</Grid>
							{user && (
								<Grid item style={{ paddingLeft: 15 }}>
									<UserAvatar />
								</Grid>
							)}
						</Grid>
					</Toolbar>
				</AppBar>
				<div className={classes.toolbar} />
				<div className={classes.editorContainer}>
					<Grid container justifyContent="center" style={{ height: "100%" }}>
						<Grid item style={{ height: "100%" }}>
							<div className={classes.mainPaper}>
								<Paper square={true} style={{ height: "100%" }} elevation={2}>
									<div id="toolbar-container" />
									<div className={classes.editorWrapper}>
										<TextToSpeechPlayer />
										<CKEditor className={classes.editor} />
									</div>
								</Paper>
							</div>
						</Grid>
					</Grid>
				</div>
			</div>
			<DesktopBuilderAppSelection />
		</>
	);
}